import React, { createContext, useContext, useState, useEffect } from 'react';
import AdminMessagePopup from '../components/admin/AdminMessagePopup';
import api, { readAuthToken } from '../services/api';

const AdminMessageContext = createContext();

export const useAdminMessages = () => {
  const context = useContext(AdminMessageContext);
  if (!context) {
    throw new Error('useAdminMessages must be used within an AdminMessageProvider');
  }
  return context;
};

export const AdminMessageProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check for new admin messages
  const checkForMessages = async () => {
    try {
      const token = readAuthToken();
      if (!token) return;

      // Only show messages on protected routes (dashboard, analytics, etc.)
      const currentPath = window.location.pathname;
      const publicRoutes = ['/', '/catalog', '/about', '/privacy-policy', '/privacy-settings'];
      if (publicRoutes.includes(currentPath)) {
        return; // Don't show messages on public pages
      }

      // Welcome message logic removed - no longer needed

      const response = await api.get('/admin/messages/check');

      const newMessages = response.data.messages || [];
      setMessages(prev => {
        const existingIds = prev.map(msg => msg._id);
        const uniqueNewMessages = newMessages.filter(msg => !existingIds.includes(msg._id));
        return [...prev, ...uniqueNewMessages];
      });
    } catch (error) {
      console.error('Error checking for admin messages:', error);
    }
  };

  // Show next unread message
  const showNextMessage = () => {
    const unreadMessages = messages.filter(msg => !msg.isRead);
    if (unreadMessages.length > 0) {
      setCurrentMessage(unreadMessages[0]);
    }
  };

  // Mark message as read
  const markAsRead = async (messageId) => {
    try {
      const token = readAuthToken();
      if (token) {
        await api.put(`/admin/messages/${messageId}/read`, {});
      }

      // If this is the welcome message, mark it as seen permanently
      if (messageId === 'welcome_msg') {
        localStorage.setItem('hasSeenWelcomeMessage', 'true');
        localStorage.removeItem('isNewUser'); // Clear the new user flag
      }

      setMessages(prev => 
        prev.map(msg => 
          msg._id === messageId ? { ...msg, isRead: true } : msg
        )
      );
    } catch (error) {
      console.error('Error marking message as read:', error);
      // Still update local state even if API fails
      if (messageId === 'welcome_msg') {
        localStorage.setItem('hasSeenWelcomeMessage', 'true');
        localStorage.removeItem('isNewUser');
      }
      
      setMessages(prev => 
        prev.map(msg => 
          msg._id === messageId ? { ...msg, isRead: true } : msg
        )
      );
    }
  };

  // Handle message action
  const handleMessageAction = async (messageId, action) => {
    try {
      const token = readAuthToken();
      if (token) {
        await api.post(`/admin/messages/${messageId}/action`, {
          action
        });
      }

      // Mark as read and remove from current view
      await markAsRead(messageId);
      setCurrentMessage(null);
      
      // Show next message if available
      setTimeout(showNextMessage, 500);
    } catch (error) {
      console.error('Error handling message action:', error);
    }
  };

  // Dismiss message (prevent it from showing again)
  const dismissMessage = async (messageId) => {
    try {
      const token = readAuthToken();
      if (token) {
        await api.post(`/admin/messages/${messageId}/dismiss`, {});
      }

      // Remove from messages list and close current message
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
      setCurrentMessage(null);
      
      // Show next message if available
      setTimeout(showNextMessage, 500);
    } catch (error) {
      console.error('Error dismissing message:', error);
    }
  };

  // Close current message
  const closeMessage = () => {
    setCurrentMessage(null);
  };

  // Check for messages on mount and set up polling
  useEffect(() => {
    // Don't check for messages on public pages or login/register pages
    const currentPath = window.location.pathname;
    const publicRoutes = ['/', '/catalog', '/about', '/privacy-policy', '/privacy-settings'];
    const excludedRoutes = ['/login', '/register'];
    
    if (publicRoutes.includes(currentPath) || excludedRoutes.includes(currentPath)) {
      return;
    }
    
    checkForMessages();
    
    // Check for new messages every 30 seconds
    const interval = setInterval(() => {
      const currentPath = window.location.pathname;
      if (!publicRoutes.includes(currentPath) && !excludedRoutes.includes(currentPath)) {
        checkForMessages();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Show next message when new messages arrive
  useEffect(() => {
    if (messages.length > 0 && !currentMessage) {
      showNextMessage();
    }
  }, [messages]);

  const value = {
    messages,
    currentMessage,
    isLoading,
    checkForMessages,
    showNextMessage,
    markAsRead,
    handleMessageAction,
    dismissMessage,
    closeMessage
  };

  return (
    <AdminMessageContext.Provider value={value}>
      {children}
      {currentMessage && (
        <AdminMessagePopup
          message={currentMessage}
          onClose={closeMessage}
          onAction={(action) => handleMessageAction(currentMessage._id, action)}
        />
      )}
    </AdminMessageContext.Provider>
  );
};
